import { Alert, Grid, Paper, Typography } from "@mui/material";
import { Formik } from "formik";
import _, { isEqual } from "lodash";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ConfirmationDialog } from "../App/Components/ConfirmationDialog/ConfirmationDialog";
import { PageContainer } from "../App/Components/PageContainer/PageContainer";
import { PageTitle } from "../App/Components/PageTitle/PageTitle";
import { CampaignPageActions, CampaignTypes } from "../App/Enums/CampaignTypes";
import {
  useDeleteCampaign,
  useEditCampaign,
  useFormDataQuery,
  useSaveCampaign,
  useUploadForm,
} from "../App/Queries/CampaignQueries";
import { useNotification } from "../App/State/Hooks/notificationHooks";
import { useAppSelector } from "../App/State/Hooks/reduxHooks";
import { selectClientValue } from "../App/State/Slices/SelectedClientSlice";
import { ICampaignFormState, ICreateCampaign, IDeleteCampaign } from "../App/Typings/Campaigns";
import { clearLocalStorageByKeyPrefix } from "../App/Utilities/browserStorage";
import {
  AreaOfInterestPattern,
  RecruiterPattern,
} from "../MessageComponents/FunctionMethods/MessageFunctions";
import {
  CampaignTypePicker,
  ContactPreferencePicker,
  CustomSubmissionInput,
  DateInputs,
  NameInput,
  SaveSuccessDialog,
  SubmitCancelButtons,
  WelcomeMessageInput,
} from "./Components/CreateEditInputs";
import FormFieldTable from "./Components/FormFieldTable";
import { getSubmitButtonLoadingText, getSubtitleMessage } from "./Helpers/CampaignConstants";
import { buildOverwriteFormInitialValues, initialFieldLoad } from "./Helpers/CampaignFunctions";
import { CampaignSchema } from "./Schema/CampaignFormSchema";
import { getCampaignBody } from "./State/CreateCampaignState";
import { InitialValues } from "./State/FormCreateInitialState";

export default function CreateCampaigns({ action }: { action: CampaignPageActions }) {
  const [submitted, setSubmitted] = useState(false);
  const [createdCampaignId, setCreatedCampaignId] = useState("");
  const [forms, setForms] = useState<object[]>([]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const { campaignId = "", formId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const importedFormState = (location.state as { importedFormState?: ICampaignFormState } | null)
    ?.importedFormState;
  const overwriteFormState = (location.state as { overwriteFormState?: ICampaignFormState } | null)
    ?.overwriteFormState;
  const { displayError, displaySuccess } = useNotification();
  const { clientId, privacyPolicyUrl, hasCareers } = useAppSelector(selectClientValue);
  const { data: formState, isLoading, error } = useFormDataQuery(campaignId, formId, action);
  const { mutate: deleteCampaign } = useDeleteCampaign();
  const { mutateAsync: editCampaignAsync, isLoading: isEditCampaignLoading } = useEditCampaign();
  const { mutateAsync: saveCampaignAsync, isLoading: isSaveCampaignLoading } = useSaveCampaign();
  const { mutateAsync: uploadFormAsync, isLoading: isUploadFormLoading } = useUploadForm();

  const isTalentNetwork =
    !isLoading && formState
      ? formState.campaignType !== CampaignTypes.Form && formState.campaignType !== CampaignTypes.Event
      : false;

  const subtitleMessage = getSubtitleMessage(action, isTalentNetwork);
  const isActionLoading = isSaveCampaignLoading || isUploadFormLoading || isEditCampaignLoading;
  const submitText = isActionLoading ? getSubmitButtonLoadingText(action, isTalentNetwork) : "Submit";
  const pageTitle = `${action} ${isTalentNetwork ? "Talent Network" : "Form"}`;

  const initial = _.cloneDeep(formState);

  if (formState) {
    const lockedCustomFields: string[] = [];
    for (let x = 0; x < formState.totalCustomFields; x++) {
      lockedCustomFields.push("customField" + x);
    }
    formState.lockedFields = lockedCustomFields;
  }

  // `importedFormState` (already normalized by buildImportedFormInitialValues when the Import
  // button built it) prefills the Create form. Kept separate from `formState` so the redirect
  // effects below (which key off the fetched `formState`) are unaffected.
  const importedInitialValues = importedFormState;

  // `overwriteFormState` (from the "Import — Overwrite" control on a form's detail page) needs
  // the destination's own live data — for the ad group remap in particular — so unlike
  // `importedFormState` above, this merge only happens once `formState` has actually loaded, not
  // before. The Edit/Duplicate loading gate below already guarantees Formik doesn't mount until
  // then, so there's no extra async sequencing to add here.
  let overwriteResult:
    | { formState: ICampaignFormState; unmatchedAdGroupCount: number; overriddenLockedFieldCount: number }
    | undefined;
  let overwriteError: string | undefined;
  if (overwriteFormState && formState) {
    try {
      overwriteResult = buildOverwriteFormInitialValues(overwriteFormState, formState);
    } catch {
      overwriteError = "There was an error applying the imported configuration to this form.";
    }
  }

  useEffect(() => {
    if (overwriteError) {
      displayError(overwriteError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overwriteError]);

  // Redirect to campaign list if campaign client does not match selected client or if there is an error
  useEffect(() => {
    if (!!clientId && !isLoading && ((formState && formState.clientId !== clientId) || error)) {
      if (error) {
        displayError("There was an error loading the campaign form");
      }
      navigate("/marketing/campaigns");
    }
  }, [clientId, formState, isLoading, navigate, error, displayError]);

  // Redirect to campaign list if action type is invalid for campaign type
  useEffect(() => {
    if (
      isTalentNetwork &&
      (action === CampaignPageActions.Create || action === CampaignPageActions.Duplicate)
    ) {
      navigate("/leadcapture/forms");
    }
  }, [isTalentNetwork, action, navigate]);

  // Redirect to campaign list if campaign client does not match selected client
  useEffect(() => {
    if (!isLoading && formState && formState.clientId !== clientId) {
      navigate("/leadcapture/forms");
    }
  }, [clientId, formState, isLoading, navigate]);

  const handleCampaignCreate = async (values: ICampaignFormState) => {
    const campaignData: ICreateCampaign = {
      name: values.campaignName,
      startDate: new Date(values.startDate),
      endDate: new Date(values.endDate),
      ongoing: values.ongoing,
    };

    //Note: This causes a warning around calling setState on an unmounted component
    //This warning is removed in react 18 and is a red herring
    const formInfo = await saveCampaignAsync(campaignData, {
      onError: () => {
        displayError("There was an error creating the form");
        Promise.reject();
      },
    });

    values.formId = formInfo.formId;
    values.campaignId = formInfo.campaignId;

    await uploadFormAsync(values, {
      onSuccess: (response) => {
        setCreatedCampaignId(response.campaignId || "");
        setForms([{ id: response.formId }]);
        setSubmitted(true);
        displaySuccess(`Form ${values.campaignName} has been successfully created!`);
      },
      onError: () => {
        if (values.formId !== undefined && values.campaignId !== undefined) {
          //This will clean up an orphaned campaign in the database
          const orphanedCampaign: IDeleteCampaign = {
            campaignId: values.campaignId,
            shouldArchiveGroup: true,
          };
          deleteCampaign(orphanedCampaign);
        }
        displayError("There was an error uploading the form");
        Promise.reject();
      },
    });
  };

  const handleCampaignEdit = async (values: ICampaignFormState) => {
    const updatedCampaign = getCampaignBody(campaignId, values);
    const initialCampaign = getCampaignBody(campaignId, formState);

    const campaignModified = !isEqual(initialCampaign, updatedCampaign);

    if (isTalentNetwork) {
      // Normally the name/date inputs are disabled, so if the user manages to change them, we
      // don't want to save them — but an Overwrite promotion is the one legitimate way those
      // values do change for a Talent Network form (see buildOverwriteFormInitialValues), and
      // `values` here already carries the promoted name/dates/ongoing straight through to
      // uploadFormAsync below. Re-pinning them to the destination here would silently revert the
      // campaign record while the form-config record kept the promoted values, desyncing the two.
      if (!overwriteResult) {
        updatedCampaign.name = initialCampaign.name;
        updatedCampaign.startDate = initialCampaign.startDate;
        updatedCampaign.endDate = initialCampaign.endDate;
        updatedCampaign.ongoing = initialCampaign.ongoing;
      }
      if (!hasCareers && updatedCampaign.campaignType !== CampaignTypes.TalentNetwork) {
        updatedCampaign.campaignType = CampaignTypes.TalentNetwork;
      }
    }

    if (campaignModified) {
      await editCampaignAsync(updatedCampaign, {
        onError: () => {
          displayError("There was an error editing the form, please try again.");
          Promise.reject();
        },
      });
    }

    await uploadFormAsync(values, {
      onSuccess: () => {
        displaySuccess("Edits were made successfully");
      },
      onError: () => {
        if (campaignModified) {
          //This may cause the campaign and form to desync,
          //but only if the campaign is uploaded successfully and the form fails
          editCampaignAsync(initialCampaign);
        }
        displayError("There was an error editing the form, please try again.");
        Promise.reject();
      },
    });

    clearLocalStorageByKeyPrefix(`campaign-responses-${campaignId}`);
  };

  const handleSubmitForm = async (values: ICampaignFormState) => {
    if (action === CampaignPageActions.Edit) {
      await handleCampaignEdit(values);
      navigate("/leadcapture/forms");
    } else {
      await handleCampaignCreate(values);
    }
  };

  if ((action === CampaignPageActions.Edit || action === CampaignPageActions.Duplicate) && isLoading) {
    return (
      <PageContainer>
        <Paper sx={{ mt: { xs: 1, md: 4 }, p: { xs: 2, lg: 4 } }}>
          <PageTitle sx={{ mb: 0 }}>Loading...</PageTitle>
        </Paper>
      </PageContainer>
    );
  } else {
    return (
      <PageContainer>
        <Paper sx={{ mt: { xs: 1, md: 4 }, p: { xs: 2, lg: 4 } }}>
          <Formik
            initialValues={
              importedInitialValues ??
              overwriteResult?.formState ??
              formState ?? {
                ...InitialValues,
                formFields: initialFieldLoad(
                  InitialValues.formFields,
                  InitialValues.campaignType,
                  InitialValues.contactPreference,
                ),
              }
            }
            validationSchema={CampaignSchema}
            onSubmit={handleSubmitForm}
          >
            {(props) => {
              const campaignModified = !_.isEqual(initial?.formFields, props.values.formFields);
              // An Overwrite is a change by definition, even when the imported file's fields
              // happen to match the destination's (e.g. re-importing just to fix the name/dates) —
              // `props.dirty` is false in that case since Formik's initialValues already are the
              // promoted overwriteResult.formState, and campaignModified only compares formFields.
              const isDirty = props.dirty || campaignModified || !!overwriteResult;
              const hasRecruiterTitleTag = hasRecruiterTags(
                props.values.welcomeMessage,
                props.values.welcomeMessageSubject,
              );
              const invalidAreaOfInterestTemplate = hasInvalidAreaOfInterestTemplate(props.values);
              const isSubmitDisabled =
                !props.isValid ||
                !privacyPolicyUrl ||
                !isDirty ||
                props.isSubmitting ||
                isActionLoading ||
                invalidAreaOfInterestTemplate ||
                hasRecruiterTitleTag;

              return (
                <form onSubmit={props.handleSubmit}>
                  <SaveSuccessDialog
                    submitted={submitted}
                    createdCampaignId={createdCampaignId}
                    forms={forms}
                    campaignName={props.values.campaignName}
                  />
                  <Grid container>
                    <Grid item xs={12} sx={{ marginBottom: "10px" }}>
                      <PageTitle
                        sx={{
                          lineHeight: 1,
                          color: "secondary.main",
                        }}
                      >
                        {pageTitle}
                      </PageTitle>
                      <Typography>{subtitleMessage}</Typography>
                      {importedFormState && (
                        <Alert severity="info" data-test="imported-form-banner" sx={{ mt: 1 }}>
                          This form was pre-filled from an imported configuration file. Review the Start/End
                          Date and any dropdown or ad group selections before submitting.
                        </Alert>
                      )}
                      {overwriteResult && (
                        <Alert severity="info" data-test="overwrite-form-banner" sx={{ mt: 1 }}>
                          This form was pre-filled from an imported configuration file, replacing its name,
                          Start/End Date, Ongoing setting, Contact Preference, messaging, and fields — review
                          before saving.
                          {overwriteResult.unmatchedAdGroupCount > 0 && (
                            <>
                              {" "}
                              {overwriteResult.unmatchedAdGroupCount} ad group selection
                              {overwriteResult.unmatchedAdGroupCount === 1 ? " doesn't" : "s don't"} exist in
                              this environment and{" "}
                              {overwriteResult.unmatchedAdGroupCount === 1 ? "was" : "were"} skipped.
                            </>
                          )}
                          {overwriteResult.overriddenLockedFieldCount > 0 && (
                            <>
                              {" "}
                              {overwriteResult.overriddenLockedFieldCount} custom field
                              {overwriteResult.overriddenLockedFieldCount === 1 ? "" : "s"} kept the current
                              Field Type instead of the file's — already has responses recorded.
                            </>
                          )}
                        </Alert>
                      )}
                    </Grid>
                    <Grid container item xs={12} gap={{ xs: 2, lg: 0 }}>
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={4}
                        sx={{ padding: { sm: "1em", md: "0 15px 0 0" } }}
                      >
                        {isTalentNetwork && <CampaignTypePicker formState={formState} />}
                        <NameInput />
                        <DateInputs />
                        <ContactPreferencePicker />
                        <WelcomeMessageInput
                          invalidAreaOfInterestTemplate={invalidAreaOfInterestTemplate}
                          hasRecruiterTitleTag={hasRecruiterTitleTag}
                        />
                        <CustomSubmissionInput />
                        <SubmitCancelButtons
                          isSubmitDisabled={isSubmitDisabled}
                          submitText={submitText}
                          onCancelClick={() => {
                            // isDirty, not props.dirty — an overwrite's initialValues are already
                            // the promoted state, so props.dirty alone would let Cancel silently
                            // discard a pending overwrite promotion with no confirmation
                            isDirty ? setShowCancelDialog(true) : navigate(-1);
                          }}
                        />
                        {!privacyPolicyUrl && (
                          <Typography variant="caption" color="error" sx={{ mt: 1 }} id="tos-error">
                            Privacy Policy is not set, please contact your account director.
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} lg={8} sx={{ padding: { xs: 0, lg: "0 15px" } }}>
                        <FormFieldTable />
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
              );
            }}
          </Formik>
        </Paper>
        <ConfirmationDialog
          handleCancel={() => navigate(-1)}
          handleConfirm={() => setShowCancelDialog(false)}
          handleClose={() => setShowCancelDialog(false)}
          key="campaign-cancel"
          cancelMessage="Continue without saving"
          confirmMessage="Continue editing"
          isOpen={showCancelDialog}
          message="Do you want to save your changes?"
          title="Wait!"
        />
      </PageContainer>
    );
  }
}

function hasInvalidAreaOfInterestTemplate(formValues: ICampaignFormState): boolean {
  const matchedAreaOfInterestTags = new Set<string>();
  for (const match of formValues.welcomeMessage.matchAll(AreaOfInterestPattern)) {
    matchedAreaOfInterestTags.add(match[0]);
  }
  for (const match of formValues.welcomeMessageSubject?.matchAll(AreaOfInterestPattern) || []) {
    matchedAreaOfInterestTags.add(match[0]);
  }
  const hasAreaOfInterestTemplate = matchedAreaOfInterestTags.size > 0;
  const areaOfInterestField = formValues.formFields["areaOfInterest"];

  const validAreaOfInterestField =
    areaOfInterestField?.enabled &&
    areaOfInterestField?.required &&
    areaOfInterestField?.dropdownOptions.length > 0 &&
    areaOfInterestField?.archived === false;

  return hasAreaOfInterestTemplate && !validAreaOfInterestField;
}

function hasRecruiterTags(...messages: (string | null | undefined)[]): boolean {
  const foundRecruiterTags = new Set<string>();
  for (const message of messages) {
    if (message) {
      for (const match of message.matchAll(RecruiterPattern)) {
        foundRecruiterTags.add(match[0]);
      }
    }
  }
  return foundRecruiterTags.size > 0;
}
